// A sanitiser for provider icon SVG markup that gets set via
// element.innerHTML on the login page (login-provider.js). It runs on every
// render regardless of whether the write-time validator
// (rapid-api-auth service/provider/icon.go, sanitizeIconSVG) accepted the
// value, so it also covers rows written before that gate existed -- this is
// the render-time half of the same defense.
//
// DOMPurify does the real work: it parses the markup as a DOM (via jsdom's
// `window` in tests, the browser's in production) and rebuilds it from an
// allowlist, rather than pattern-matching the raw string. That closes gaps
// the old regex-only approach had -- e.g. SVG SMIL animation elements
// (<set attributeName="onclick" to="...">, <animate>, <animateTransform>)
// can rewrite/inject attributes at runtime without ever appearing as an
// on*= string, so a regex over "on\w+=" never saw them. FORBID_TAGS below
// removes those alongside the constructs the old sanitiser targeted
// (<script>, <foreignObject>, <use> -- which can pull in an external/
// data: document); FORBID_ATTR removes href/xlink:href outright rather than
// only stripping non-fragment values, since ADD_ATTR is left empty and no
// href use case survives in the icon markup the fleet actually has today.
//
// The previous regex pass is kept below as a cheap second layer over
// DOMPurify's output (belt and braces): DOMPurify is a battle-tested,
// actively-maintained library, but a second, independent, dependency-free
// pass costs nothing and still catches anything a future DOMPurify config
// mistake (e.g. an accidentally-broadened profile) might let through.
import DOMPurify from 'dompurify'

const DOMPURIFY_CONFIG = {
  USE_PROFILES: { svg: true, svgFilters: true },
  FORBID_TAGS: ['script', 'foreignObject', 'style', 'set', 'animate', 'animateTransform', 'use'],
  FORBID_ATTR: ['href', 'xlink:href'],
  ADD_ATTR: []
}

export const sanitizeIconSVG = svg => {
  if (!svg) return ''

  let result = DOMPurify.sanitize(String(svg), DOMPURIFY_CONFIG)

  // Second layer: the same regex-based checks the original implementation
  // used, run over DOMPurify's already-cleaned output.
  result = result.replace(/<script\b[^>]*>[\s\S]*?<\/script\s*>/gi, '')
  result = result.replace(/<script\b[^>]*\/?>/gi, '')

  result = result.replace(/<foreignObject\b[^>]*>[\s\S]*?<\/foreignObject\s*>/gi, '')
  result = result.replace(/<foreignObject\b[^>]*\/?>/gi, '')

  result = result.replace(/\son\w+\s*=\s*"[^"]*"/gi, '')
  result = result.replace(/\son\w+\s*=\s*'[^']*'/gi, '')
  result = result.replace(/\son\w+\s*=\s*[^\s"'>]+/gi, '')

  result = result.replace(/\s(?:xlink:)?href\s*=\s*"(?!#)[^"]*"/gi, '')
  result = result.replace(/\s(?:xlink:)?href\s*=\s*'(?!#)[^']*'/gi, '')

  result = result.replace(/javascript:/gi, '')

  return result.trim()
}
